import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBackRoute } from '../hooks/useBackRoute';
import { navigateWithFrom } from '../utils/navigationUtils';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeString, sanitizeSearchInput } from '../utils/security';
import { fetchServiceRecords } from '../services/serviceRecordsService';
import TableListFooter from '../components/TableListFooter';
import { fetchUserProfile } from '../services/authService';
import DateRangeFilterModal from '../components/DateRangeFilterModal';
import { getOnemRowClass } from '../utils/onemDerecesiHelper';

const rowsPerPageOptions = [10, 25, 50, 100];

const safeText = (value) => sanitizeString(value ?? '');

// Safe date parsing ve formatting
const formatDateTime = (dateValue) => {
  if (!dateValue) return '—';
  
  try {
    let date;
    
    if (dateValue && typeof dateValue === 'object' && dateValue.$date) {
      date = new Date(dateValue.$date);
    } 
    else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    }
    else if (typeof dateValue === 'number') {
      date = new Date(dateValue);
    }
    else if (dateValue instanceof Date) {
      date = dateValue;
    }
    else if (typeof dateValue === 'object' && dateValue !== null) {
      if (dateValue.toISOString) {
        date = dateValue;
      } else if (dateValue.toString && dateValue.toString() !== '[object Object]') {
        date = new Date(dateValue.toString());
      } else {
        console.warn('[formatDateTime] Unknown date format:', dateValue);
        return '—';
      }
    }
    else {
      return '—';
    }
    
    if (!date || isNaN(date.getTime())) {
      console.warn('[formatDateTime] Invalid date:', dateValue);
      return '—';
    }
    
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    console.error('[formatDateTime] Error:', e, dateValue);
    return '—';
  }
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;

  if (error?.status === 404) return 'API endpoint bulunamadı.';
  if (error?.status === 401) return 'Kimlik doğrulama hatası. Lütfen tekrar giriş yapın.';
  if (error?.status === 403) return 'Bu işlem için yetkiniz yok.';

  let message =
    error?.message ||
    error?.data?.mesaj ||
    error?.data?.message ||
    error?.data?.error ||
    error?.data?.hata ||
    fallback;

  if (message && typeof message === 'string') {
    message = message
      .replace(/&#x27;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }

  if (message === 'Failed to fetch') {
    return 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.';
  }

  return message || fallback;
};

// techServiceId'yi user objesinden çıkar (string, object veya nested yapıları destekler)
const extractTechServiceId = (user) => {
  if (!user) return null;
  const ts = user.techService ?? user.techServiceId;
  if (!ts) return null;
  if (typeof ts === 'string' && ts.trim()) return ts.trim();
  if (ts && typeof ts === 'object') {
    const id = ts._id ?? ts.id;
    if (id) return typeof id === 'string' ? id : String(id);
  }
  return null;
};

const TechServiceServiceRecordsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useBackRoute('/');
  const { user, isLoading: authLoading, setUser } = useAuth();
  
  const [records, setRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({ search: '', islemTipi: '' });
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [profileRefetched, setProfileRefetched] = useState(false);
  const [dateRange, setDateRange] = useState({});
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);

  // Kullanıcının teknik servis ID'sini al (nested yapıları destekler)
  const techServiceId = useMemo(() => extractTechServiceId(user), [user]);

  const sanitizedSearchValue = useMemo(() => {
    const sanitized = sanitizeSearchInput(filters.search);
    return sanitized && sanitized.trim().length > 0 ? sanitized : undefined;
  }, [filters.search]);

  const filterParams = useMemo(
    () => ({
      search: sanitizedSearchValue,
      islemTipi: filters.islemTipi || undefined,
      baslangicTarihi: dateRange.baslangicTarihi,
      bitisTarihi: dateRange.bitisTarihi,
    }),
    [sanitizedSearchValue, filters.islemTipi, dateRange.baslangicTarihi, dateRange.bitisTarihi]
  );

  // Randevu bekleniyor kayıtlarını yükle
  const loadRecords = useCallback(async () => {
    // Auth yüklenene kadar bekle
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!user) {
      setError('Kullanıcı bilgisi bulunamadı. Lütfen tekrar giriş yapın.');
      setLoading(false);
      return;
    }

    if (!techServiceId) {
      setError('Teknik servis bilgisi bulunamadı. Lütfen yönetici ile iletişime geçin.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const requestParams = {
        page: currentPage,
        limit: rowsPerPage,
        aktif: true,
        ...filterParams,
        teknikServisId: techServiceId, // Backend'de bu filtre ile kendi teknik servislerine atanmış kayıtları getir
      };
      
      const response = await fetchServiceRecords(requestParams);
      const list = response?.veri?.servisKayitlari || response?.veri || [];
      const pagination = response?.veri?.sayfalama || {};
      
      // Backend'den zaten oluşturulma tarihine göre artan sıralı geliyor (en eski en üstte)
      setRecords(list);
      setPaginationMeta({
        total: pagination.toplam || list.length,
        totalPages: pagination.toplamSayfa || Math.ceil(list.length / rowsPerPage) || 1,
      });
    } catch (err) {
      setError(getErrorMessage(err, 'Servis kayıtları yüklenirken hata oluştu'));
      setRecords([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  }, [techServiceId, currentPage, rowsPerPage, filterParams, user, authLoading]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  // techServiceId yoksa ama kullanıcı tech-service rolündeyse profili yeniden çek (ilk yükleme zamanlama sorunu)
  useEffect(() => {
    if (authLoading || !user || techServiceId || profileRefetched) return;
    const rol = user.rol || user.role || '';
    if (rol !== 'tech-service') return;

    let cancelled = false;
    const refetchProfile = async () => {
      try {
        const res = await fetchUserProfile();
        const updatedUser = res?.veri?.kullanici ?? res?.veri ?? res?.kullanici ?? res;
        if (cancelled || !updatedUser) return;
        if (extractTechServiceId(updatedUser)) {
          setUser(updatedUser);
        }
      } catch {
        // Sessizce devam et
      } finally {
        if (!cancelled) setProfileRefetched(true);
      }
    };
    refetchProfile();
    return () => { cancelled = true; };
  }, [authLoading, user, techServiceId, profileRefetched, setUser]);

  const handleSearchChange = (event) => {
    const value = sanitizeSearchInput(event.target.value, { trimEdges: false });
    setSearchInput(value);
  };

  // Debounce için search input
  useEffect(() => {
    if (searchInput.trim().length === 0) {
      setSearchLoading(false);
      setFilters((prev) => ({ ...prev, search: '' }));
      return;
    }

    const timeoutId = setTimeout(() => {
      setSearchLoading(true);
      setFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
    setCurrentPage(1);
  };

  const getStatusBadge = (durum) => {
    const statusColors = {
      'Randevu Bekleniyor': '#f59e0b',
      'Randevu Tarihi Bekleniyor': '#3b82f6',
      'Randevu Süresi Geçmiş': '#dc2626',
      'İşlemde': '#10b981',
      'Randevu Tamamlandı': '#059669',
      'Randevu İptal Edildi': '#ef4444',
      'Tamamlandı': '#059669',
    };
    
    const color = statusColors[durum] || '#64748b';
    
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 500,
        background: `${color}15`,
        color: color,
        border: `1px solid ${color}40`,
        whiteSpace: 'nowrap',
        display: 'inline-block',
      }}>
        {durum || '—'}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityColors = {
      'Acil': '#ef4444',
      'Yüksek': '#f59e0b',
      'Orta': '#3b82f6',
      'Düşük': '#64748b',
    };
    
    const color = priorityColors[priority] || '#64748b';
    
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 500,
        background: `${color}15`,
        color: color,
        border: `1px solid ${color}40`
      }}>
        {priority || '—'}
      </span>
    );
  };

  const dateFilterLabel = dateRange.baslangicTarihi || dateRange.bitisTarihi
    ? `${dateRange.baslangicTarihi || '…'} – ${dateRange.bitisTarihi || '…'}`
    : null;

  const renderTable = () => {
    if (loading) {
      return (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Servis kayıtları yükleniyor</div>
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      );
    }

    if (error) return <div className="error-state">{safeText(error)}</div>;
    
    if (records.length === 0) {
      return (
        <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
          <i className="fas fa-clipboard-list" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
          <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Randevu bekleniyor durumunda servis kaydı bulunamadı</div>
        </div>
      );
    }

    return (
      <table className="admins-table">
        <thead>
          <tr>
            <th>Müşteri</th>
            <th>İşlem Tipi</th>
            <th>Önem Derecesi</th>
            <th>Durum</th>
            <th>Oluşturma Tarihi</th>
            <th className="islemler-sutunu">İşlemler</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record) => {
            const recordId = record.id || record._id;
            const customer = record.musteri || {};
            const customerName = `${customer.adi || customer.name || ''} ${customer.soyadi || customer.surname || ''}`.trim() || '—';
            
            return (
              <tr key={recordId} className={getOnemRowClass(record.onemDerecesi) || undefined}>
                <td>
                  <span
                    className="tech-service-name-link"
                    onClick={() => navigateWithFrom(navigate, `/service-records/${recordId}/update`, location)}
                    style={{ cursor: 'pointer' }}
                  >
                    {customerName}
                  </span>
                </td>
                <td>{record.islemTipi || '—'}</td>
                <td>{getPriorityBadge(record.onemDerecesi)}</td>
                <td>{getStatusBadge(record.durum)}</td>
                <td>
                  {formatDateTime(record.olusturmaTarihi || record.createdAt)}
                </td>
                <td className="table-actions islemler-sutunu">
                  <button
                    type="button"
                    className="category-action-btn edit-btn"
                    onClick={() => navigateWithFrom(navigate, `/service-records/${recordId}/update`, location)}
                    title="Randevu Oluştur"
                  >
                    <i className="fas fa-calendar-plus"></i>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  // Auth yükleniyorsa loading göster
  if (authLoading) {
    return (
      <main className="main-content tech-service-service-records">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Yükleniyor...</div>
        </div>
      </main>
    );
  }

  // Teknik servis ID yoksa: profili yeniden çekmeyi denediysek hata göster, yoksa kısa loading
  if (!techServiceId && user) {
    const isTechServiceRole = (user.rol || user.role || '') === 'tech-service';
    if (isTechServiceRole && !profileRefetched) {
      return (
        <main className="main-content tech-service-service-records">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <div className="loading-text">Profil bilgisi yükleniyor...</div>
          </div>
        </main>
      );
    }
    return (
      <main className="main-content tech-service-service-records">
        <div className="content-header">
          <h1>Servis Kayıtları</h1>
        </div>
        <div className="error-state">
          Teknik servis bilgisi bulunamadı. Lütfen yönetici ile iletişime geçin.
        </div>
      </main>
    );
  }

  return (
    <main className="main-content tech-service-service-records">
      <div className="content-header">
        <div>
          <button
            type="button"
            className="secondary-btn"
            onClick={goBack}
            style={{ marginBottom: '16px' }}
          >
            <i className="fas fa-arrow-left"></i>
            <span>Geri Dön</span>
          </button>
          <h1>Servis Kayıtları</h1>
        </div>
      </div>

      <div className="toolbar">
        <div className="search-filter-bar" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="search-box" style={{ flex: 1, minWidth: '250px' }}>
            {searchLoading ? (
              <span style={{ 
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none'
              }}>
                <i className="fas fa-spinner fa-spin" style={{ 
                  color: 'var(--text-gray)'
                }}></i>
              </span>
            ) : (
              <i className="fas fa-search"></i>
            )}
            <input
              type="text"
              placeholder="Müşteri adı, telefon, servis no"
              value={searchInput}
              onChange={handleSearchChange}
              maxLength={100}
              autoComplete="off"
            />
          </div>
          
          <select
            value={filters.islemTipi}
            onChange={(e) => handleFilterChange('islemTipi', e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              fontSize: '14px',
              background: '#ffffff',
              minWidth: '150px'
            }}
          >
            <option value="">Tüm İşlem Tipleri</option>
            <option value="Kurulum">Kurulum</option>
            <option value="Bakım">Bakım</option>
            <option value="Arıza">Arıza</option>
          </select>

          <button
            type="button"
            className={`secondary-btn ${dateFilterLabel ? 'filter-active' : ''}`}
            onClick={() => setIsDateModalOpen(true)}
          >
            <i className="fas fa-filter"></i>
            <span>{dateFilterLabel || 'Tarih Filtrele'}</span>
          </button>
        </div>
      </div>

      <div className="table-container" style={{ background: 'transparent', border: 'none' }}>
        <div style={{
          background: '#fff',
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <div style={{ overflow: 'hidden' }}>
            {renderTable()}
          </div>
          
          <TableListFooter
            totalRecords={paginationMeta.total}
            currentPage={currentPage}
            totalPages={paginationMeta.totalPages || 1}
            onPageChange={setCurrentPage}
            loading={loading}
            embedded
          />
        </div>
      </div>

      <DateRangeFilterModal
        isOpen={isDateModalOpen}
        onClose={() => setIsDateModalOpen(false)}
        initialRange={dateRange}
        onApply={(range) => {
          setDateRange(range);
          setCurrentPage(1);
        }}
      />
    </main>
  );
};

export default TechServiceServiceRecordsPage;
